import React from "react";
import { container } from "../styles/commonStyles";

const SettingsIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"></circle>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
  </svg>
);

const InputScreen = ({
  theme,
  setTheme,
  muted,
  toggleSoundMute,
  showMeaningFeedback,
  toggleMeaningFeedback,
  user,
  goHome,
  handleOpenProfile,
  handleSetupStart,
  selectedSet,
  setSelectedSet,
  gameType,
  gameLength,
  setGameLength,
  playMode,
  setPlayMode,
  isStarting
}) => {
  const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);
  const isDark = theme === "dark";

  const colors = {
    background: isDark ? "linear-gradient(180deg, #0F172A 0%, #1E293B 100%)" : "linear-gradient(180deg, #1F8A8A 0%, #4FB6B2 25%, #9FE3DE 55%, #EAF7F6 85%, #F7FCFB 100%)",
    surface: isDark ? "#1E293B" : "#FFFFFF",
    text: isDark ? "#F1F5F9" : "#1E293B",
    secondaryText: isDark ? "#94A3B8" : "#64748B",
    mutedSurface: isDark ? "#0F172A" : "#F1F5F9",
    border: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"
  };

  return (
    <div
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.repeat && !isStarting) {
          handleSetupStart();
        }
      }}
      style={{
        ...container,
        justifyContent: "flex-start",
        padding: "0",
        height: "auto",
        minHeight: "100vh",
        overflowY: "auto",
        gap: "0",
        background: colors.background
      }}
    >
      {/* --- SECTION 1: Top Bar --- */}
      <div style={{
        width: "100%", maxWidth: "420px", height: "56px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 16px", marginTop: "16px"
      }}>
        <button
          onClick={goHome}
          style={{
            width: "40px", height: "40px", borderRadius: "50%",
            background: "rgba(255,255,255,0.15)", border: "none",
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "white"
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>

        <button
          onClick={() => setIsSettingsOpen(true)}
          style={{
            width: "40px", height: "40px", borderRadius: "50%",
            background: "rgba(255,255,255,0.15)", border: "none",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "white", cursor: "pointer", padding: 0
          }}
        >
          <SettingsIcon />
        </button>
      </div>

      {/* --- SECTION 2: Header Wrapper --- */}
      <div style={{
        textAlign: "center",
        marginTop: "4px",
        width: "100%",
        maxWidth: "420px"
      }}>
        <h1 style={{
          margin: 0,
          fontSize: "34px",
          fontWeight: "800",
          color: "white",
          letterSpacing: "-0.5px"
        }}>
          Game Setup
        </h1>
        <p style={{
          margin: "8px auto 0",
          fontSize: "15px",
          color: "rgba(255,255,255,0.85)",
          maxWidth: "82%",
          lineHeight: "1.4"
        }}>
          Choose your mode and jump in
        </p>
      </div>

      {/* --- SECTION 3: Content sections --- */}
      <div style={{ width: "100%", maxWidth: "420px", padding: "0 20px", marginTop: "20px" }}>
        {/* Practice Set */}
        <div style={{ width: "100%", marginBottom: "16px" }}>
          <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.8)", marginBottom: "8px", fontWeight: "600" }}>Practice Set</div>
          <div style={{
            background: colors.surface, borderRadius: "20px", padding: "12px 16px",
            boxShadow: isDark ? "0 10px 30px rgba(0,0,0,0.25)" : "0 15px 35px rgba(0,0,0,0.06)",
            display: "flex", alignItems: "center", gap: "12px", position: "relative",
            border: isDark ? "1px solid #334155" : "none"
          }}>
            <div style={{ width: "40px", height: "40px", background: colors.mutedSurface, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
              <img src="/images/game_s/quick practise.webp" alt="Notebook" style={{ width: "100%", height: "100%", objectFit: "contain", transform: "scale(1.2)" }} />
            </div>
            <div style={{ flex: 1 }}>
              <select
                value={selectedSet}
                onChange={(e) => setSelectedSet(e.target.value)}
                style={{
                  width: "100%", background: "transparent", border: "none", outline: "none",
                  fontSize: "16px", fontWeight: "700", color: colors.text, cursor: "pointer",
                  appearance: "none", paddingRight: "30px"
                }}
              >
                <option value="mixed" style={{ background: colors.surface }}>Quick Practice (Mixed)</option>
                <option value="review" style={{ background: colors.surface }}>Review Mistakes</option>
              </select>
            </div>
            <div style={{ position: "absolute", right: "16px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: colors.secondaryText }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
          </div>
        </div>

        {/* Mode */}
        <div style={{ width: "100%", marginBottom: "16px" }}>
          <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.8)", marginBottom: "8px", fontWeight: "600" }}>Mode</div>
          <div style={{
            background: colors.surface, borderRadius: "18px", padding: "6px",
            boxShadow: isDark ? "0 10px 30px rgba(0,0,0,0.25)" : "0 15px 35px rgba(0,0,0,0.06)",
            display: "flex", gap: "6px",
            border: isDark ? "1px solid #334155" : "none"
          }}>
            <button
              onClick={() => setGameLength("quick")}
              style={{
                flex: 1, border: "none", borderRadius: "14px", padding: "12px 8px", cursor: "pointer",
                background: gameLength === "quick" ? "rgba(20,184,166,0.15)" : "transparent",
                transition: "all 0.3s ease", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px"
              }}
            >
              <div style={{ width: "22px", height: "22px", color: "#14B8A6" }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
                </svg>
              </div>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: "15px", fontWeight: "700", color: colors.text }}>Quick</div>
                <div style={{ fontSize: "12px", color: gameLength === "quick" ? "#14B8A6" : colors.secondaryText }}>10 words</div>
              </div>
            </button>
            <button
              onClick={() => setGameLength("standard")}
              style={{
                flex: 1, border: "none", borderRadius: "14px", padding: "12px 8px", cursor: "pointer",
                background: gameLength === "standard" ? "rgba(20,184,166,0.15)" : "transparent",
                transition: "all 0.3s ease", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px"
              }}
            >
              <div style={{ width: "22px", height: "22px", color: gameLength === "standard" ? "#14B8A6" : colors.secondaryText }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: "15px", fontWeight: "700", color: colors.text }}>Standard</div>
                <div style={{ fontSize: "12px", color: gameLength === "standard" ? "#14B8A6" : colors.secondaryText }}>20 words</div>
              </div>
            </button>
          </div>
        </div>

        {/* Play Type */}
        <div style={{ width: "100%", marginBottom: "24px" }}>
          <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.8)", marginBottom: "8px", fontWeight: "600" }}>Play Type</div>
          <div style={{ display: "flex", gap: "12px" }}>
            <div
              onClick={() => setPlayMode("quick")}
              style={{
                flex: 1, backgroundColor: colors.surface, borderRadius: "20px", padding: "20px 12px", cursor: "pointer",
                boxShadow: playMode === "quick" ? (isDark ? "0 10px 30px rgba(20,184,166,0.2)" : "0 15px 35px rgba(20,184,166,0.15)") : (isDark ? "0 10px 30px rgba(0,0,0,0.2)" : "0 15px 35px rgba(0,0,0,0.06)"),
                display: "flex", flexDirection: "column", alignItems: "center", gap: "12px",
                position: "relative", border: playMode === "quick" ? "2px solid #14B8A6" : (isDark ? "1px solid #334155" : "none"),
                transition: "all 0.3s ease", transform: playMode === "quick" ? "scale(1.02)" : "scale(1)",
                opacity: playMode === "quick" ? 1 : 0.8
              }}
            >
              <div style={{ position: "absolute", top: "10px", right: "10px" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill={playMode === "quick" ? "#14B8A6" : "transparent"} stroke={playMode === "quick" ? "none" : "#E2E8F0"} strokeWidth="2"></circle>
                  {playMode === "quick" && <polyline points="16 9 11 14 8 11" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"></polyline>}
                </svg>
              </div>
              <div style={{ width: "64px", height: "64px", background: isDark ? "#451a03" : "#F6E7B0", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                <img src="/images/game_s/play.webp" alt="Play" style={{ width: "100%", height: "100%", objectFit: "contain", transform: "scale(1.1)" }} />
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "17px", fontWeight: "700", color: colors.text }}>Play</div>
                <div style={{ fontSize: "13px", color: colors.secondaryText, marginTop: "2px" }}>Jump straight in</div>
              </div>
            </div>
            <div
              onClick={() => setPlayMode("learn")}
              style={{
                flex: 1, backgroundColor: colors.surface, borderRadius: "20px", padding: "20px 12px", cursor: "pointer",
                boxShadow: playMode === "learn" ? (isDark ? "0 10px 30px rgba(20,184,166,0.2)" : "0 15px 35px rgba(20,184,166,0.15)") : (isDark ? "0 10px 30px rgba(0,0,0,0.2)" : "0 15px 35px rgba(0,0,0,0.06)"),
                display: "flex", flexDirection: "column", alignItems: "center", gap: "12px",
                position: "relative", border: playMode === "learn" ? "2px solid #14B8A6" : (isDark ? "1px solid #334155" : "none"),
                transition: "all 0.3s ease", transform: playMode === "learn" ? "scale(1.02)" : "scale(1)",
                opacity: playMode === "learn" ? 1 : 0.8,
                overflow: "hidden"
              }}
            >
              {/* Recommended Ribbon */}
              <div style={{
                position: "absolute",
                top: "32px",
                right: "-38px",
                width: "140px",
                background: "linear-gradient(135deg, #60A5FA, #2563EB)",
                color: "white",
                fontSize: "7.5px",
                fontWeight: "900",
                padding: "4px 0",
                paddingRight: "8px",
                transform: "rotate(45deg)",
                textAlign: "center",
                boxShadow: "0 4px 10px rgba(37,99,235,0.3)",
                zIndex: 5,
                letterSpacing: "0.6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
                textShadow: "0 1px 2px rgba(0,0,0,0.2)"
              }}>
                <span style={{ fontSize: "9px" }}>✦</span>
                RECOMMENDED
                <span style={{ fontSize: "9px" }}>✦</span>
              </div>

              <div style={{ position: "absolute", top: "10px", right: "10px", zIndex: 6 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill={playMode === "learn" ? "#14B8A6" : "transparent"} stroke={playMode === "learn" ? "none" : "#E2E8F0"} strokeWidth="2"></circle>
                  {playMode === "learn" && <polyline points="16 9 11 14 8 11" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"></polyline>}
                </svg>
              </div>
              <div style={{ width: "64px", height: "64px", background: isDark ? "#1e3a8a" : "#DCEAF7", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                <img src="/images/game_s/learn and play.webp" alt="Learn & Play" style={{ width: "100%", height: "100%", objectFit: "contain", transform: "scale(1.1)" }} />
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "17px", fontWeight: "700", color: colors.text }}>Learn & Play</div>
                <div style={{ fontSize: "13px", color: colors.secondaryText, marginTop: "2px" }}>Tutorial + Quiz</div>
              </div>
            </div>
          </div>
        </div>

        {/* Start Button Container */}
        <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", marginTop: "16px" }}>
          <button
            onClick={handleSetupStart}
            disabled={isStarting}
            style={{
              width: "100%", height: "60px", background: isStarting ? (isDark ? "#1E293B" : "#E2E8F0") : "linear-gradient(135deg, #14B8A6, #0EA5A4)",
              border: "none", borderRadius: "24px",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: isStarting ? colors.secondaryText : "white", cursor: isStarting ? "not-allowed" : "pointer",
              boxShadow: isStarting ? "none" : "0 12px 30px rgba(20,184,166,0.35)",
              transition: "all 0.2s ease"
            }}
          >
            <span style={{ fontSize: "20px", fontWeight: "800", letterSpacing: "0.5px" }}>
              {isStarting ? "Starting..." : "Start Game"}
            </span>
          </button>
        </div>
      </div>

      {/* --- SETTINGS POPUP --- */}
      {isSettingsOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0, 0, 0, 0.7)", backdropFilter: "blur(8px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 2000, padding: "20px"
        }} onClick={() => setIsSettingsOpen(false)}>
          <div style={{
            background: colors.surface, borderRadius: "32px", padding: "32px",
            width: "100%", maxWidth: "380px", boxShadow: "0 25px 50px rgba(0,0,0,0.3)",
            display: "flex", flexDirection: "column", gap: "24px",
            border: isDark ? "1px solid #334155" : "none",
            animation: "popupFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)"
          }} onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: "center" }}>
              <h2 style={{ fontSize: "28px", fontWeight: "900", color: colors.text, margin: 0, letterSpacing: "-0.5px" }}>Setup Settings</h2>
              <p style={{ fontSize: "14px", color: colors.secondaryText, fontWeight: "500", marginTop: "4px" }}>Configure your session, {user.username}</p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {/* Theme Toggle */}
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                background: isDark ? "#0F172A" : "#F8FAFC", padding: "16px", borderRadius: "20px", border: `1px solid ${isDark ? '#334155' : '#F1F5F9'}`
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{ width: "40px", height: "40px", background: colors.surface, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 6px rgba(0,0,0,0.03)" }}>
                    <span style={{ fontSize: "20px" }}>{isDark ? "🌙" : "☀️"}</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontSize: "16px", fontWeight: "700", color: colors.text }}>Dark Mode</span>
                    <span style={{ fontSize: "12px", color: colors.secondaryText }}>{isDark ? "Night" : "Light"} theme</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    const next = isDark ? "light" : "dark";
                    setTheme(next);
                    localStorage.setItem("app_theme", next);
                  }}
                  style={{
                    width: "54px", height: "30px", borderRadius: "99px",
                    background: isDark ? "#10B981" : "#E2E8F0",
                    border: "none", position: "relative", cursor: "pointer", transition: "all 0.3s ease"
                  }}
                >
                  <div style={{
                    width: "24px", height: "24px", background: "white", borderRadius: "50%",
                    position: "absolute", top: "3px", left: isDark ? "27px" : "3px", transition: "all 0.3s ease",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                  }} />
                </button>
              </div>

              {/* Sound Toggle */}
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                background: isDark ? "#0F172A" : "#F8FAFC", padding: "16px", borderRadius: "20px", border: `1px solid ${isDark ? '#334155' : '#F1F5F9'}`
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{ width: "40px", height: "40px", background: colors.surface, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 6px rgba(0,0,0,0.03)" }}>
                    <span style={{ fontSize: "20px" }}>{muted ? "🔇" : "🔊"}</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontSize: "16px", fontWeight: "700", color: colors.text }}>Game Sounds</span>
                    <span style={{ fontSize: "12px", color: colors.secondaryText }}>SFX and feedback</span>
                  </div>
                </div>
                <button
                  onClick={toggleSoundMute}
                  style={{
                    width: "54px", height: "30px", borderRadius: "99px",
                    background: muted ? "#E2E8F0" : "#10B981",
                    border: "none", position: "relative", cursor: "pointer", transition: "all 0.3s ease"
                  }}
                >
                  <div style={{
                    width: "24px", height: "24px", background: "white", borderRadius: "50%",
                    position: "absolute", top: "3px", left: muted ? "3px" : "27px", transition: "all 0.3s ease",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                  }} />
                </button>
              </div>

              {/* Meaning Feedback Toggle */}
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                background: isDark ? "#0F172A" : "#F8FAFC", padding: "16px", borderRadius: "20px", border: `1px solid ${isDark ? '#334155' : '#F1F5F9'}`
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{ width: "40px", height: "40px", background: colors.surface, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 6px rgba(0,0,0,0.03)" }}>
                    <span style={{ fontSize: "20px" }}>💡</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontSize: "16px", fontWeight: "700", color: colors.text }}>Show Meanings</span>
                    <span style={{ fontSize: "12px", color: colors.secondaryText }}>After wrong answers</span>
                  </div>
                </div>
                <button
                  onClick={toggleMeaningFeedback}
                  style={{
                    width: "54px", height: "30px", borderRadius: "99px",
                    background: !showMeaningFeedback ? "#E2E8F0" : "#10B981",
                    border: "none", position: "relative", cursor: "pointer", transition: "all 0.3s ease"
                  }}
                >
                  <div style={{
                    width: "24px", height: "24px", background: "white", borderRadius: "50%",
                    position: "absolute", top: "3px", left: !showMeaningFeedback ? "3px" : "27px", transition: "all 0.3s ease",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                  }} />
                </button>
              </div>
            </div>

            <button
              onClick={() => setIsSettingsOpen(false)}
              style={{
                width: "100%", background: "#10B981", color: "white", border: "none",
                borderRadius: "18px", padding: "18px", fontSize: "18px", fontWeight: "800",
                cursor: "pointer", boxShadow: "0 8px 20px rgba(16, 185, 129, 0.2)",
                marginTop: "8px"
              }}
            >
              Confirm Setup
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes popupFadeIn {
          from { opacity: 0; transform: scale(0.9) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default InputScreen;
